import { formatUnits } from "viem";
import { useCurveStats } from "@/lib/curve-stats";

function fmt(v: bigint | undefined, d = 0): string {
  if (v === undefined) return "—";
  return Number(formatUnits(v, 18)).toLocaleString("en-US", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}

// A live, read-only graphic for the Trade buy flow: how much 01 is gone from
// supply. Pre-bond that is the running fee burn (minted minus totalSupply) as
// a share of circulating. Once bonded nothing can ever mint again, so the
// honest figure becomes the hard cap minus totalSupply: everything the fee
// has burned plus the remainder above S_MAX that no one can mint. All real
// on-chain reads; the bar shows a dash until they load. The hook is deduped
// by react-query, so this adds no extra RPC calls.
export function BurnSupplyMeter() {
  const stats = useCurveStats();
  const bonded = stats.bonded === true;
  const gone = bonded ? stats.removed : stats.burned;
  const base = bonded ? stats.maxSupply : stats.minted;
  const ready = gone !== undefined && base !== undefined && base > 0n;
  const pct = ready ? Number((gone! * 100000n) / base!) / 1000 : undefined;
  // keep a visible sliver when the share is tiny but non-zero
  const width =
    pct !== undefined ? Math.min(100, Math.max(pct, pct > 0 ? 0.75 : 0)) : 0;
  return (
    <div className="shsupply">
      <div className="shsupply-head">
        <span className="shsupply-label">
          {bonded ? "01 gone from supply forever" : "01 burned so far"}
        </span>
        <span className="shsupply-pct mono">
          {pct !== undefined ? pct.toFixed(pct < 1 ? 2 : 1) + "%" : "—"}
        </span>
      </div>
      <div
        className="shsupply-track"
        role="img"
        aria-label={
          pct !== undefined
            ? bonded
              ? `${pct.toFixed(2)} percent of the 21 million 01 cap is gone forever, burned in trading or never mintable again`
              : `${pct.toFixed(2)} percent of circulating 01 has been burned by the curve`
            : "burned supply loading"
        }
      >
        <span className="shsupply-fill burn" style={{ width: width + "%" }} />
      </div>
      <p className="shsupply-sub">
        {bonded ? (
          <>
            <span className="mono">{fmt(stats.removed)}</span> of the{" "}
            <span className="mono">{fmt(stats.maxSupply)}</span> 01 cap: burned in trading
            plus 01 no one can ever mint
          </>
        ) : (
          <>
            <span className="mono">{fmt(stats.burned)}</span> of{" "}
            <span className="mono">{fmt(stats.minted)}</span> 01 removed from supply
          </>
        )}
      </p>
    </div>
  );
}
